/** Pure unit conversion/formatting helpers — no Angular dependencies. */

export type UnitSystem = 'metric' | 'imperial';

/**
 * Measurement kinds the app can localize. Keys name the METRIC SOURCE UNIT the
 * API/value is expected to be in (lengthCm takes centimetres, distanceKm takes
 * kilometres, ...) so a call site can't silently feed the wrong scale.
 * Temperature is the only kind rendered today; the rest are pre-wired so a new
 * display is just `value | measure: '<quantity>'`.
 */
export type Quantity =
  | 'temperature' // °C → °F
  | 'lengthMm' // mm → in
  | 'lengthCm' // cm → in
  | 'lengthM' // m → ft
  | 'distanceKm' // km → mi
  | 'speedKmh' // km/h → mph
  | 'massKg'; // kg → lb

interface QuantityDef {
  /** Metric source value → imperial value. */
  toImperial: (metric: number) => number;
  metricUnit: string;
  imperialUnit: string;
  /** Decimal places shown after conversion (imperial only; metric passes through). */
  imperialDecimals: number;
}

const QUANTITIES: Record<Quantity, QuantityDef> = {
  temperature: {
    toImperial: (c) => (c * 9) / 5 + 32,
    metricUnit: '°C',
    imperialUnit: '°F',
    // Device sensors report whole degrees; keep imperial whole too.
    imperialDecimals: 0,
  },
  // Precipitation-scale lengths (a few mm) need two decimals to survive the in conversion.
  lengthMm: { toImperial: (mm) => mm / 25.4, metricUnit: 'mm', imperialUnit: 'in', imperialDecimals: 2 },
  lengthCm: { toImperial: (cm) => cm / 2.54, metricUnit: 'cm', imperialUnit: 'in', imperialDecimals: 1 },
  lengthM: { toImperial: (m) => m * 3.28084, metricUnit: 'm', imperialUnit: 'ft', imperialDecimals: 1 },
  distanceKm: { toImperial: (km) => km / 1.609344, metricUnit: 'km', imperialUnit: 'mi', imperialDecimals: 1 },
  speedKmh: { toImperial: (kmh) => kmh / 1.609344, metricUnit: 'km/h', imperialUnit: 'mph', imperialDecimals: 0 },
  massKg: { toImperial: (kg) => kg * 2.204623, metricUnit: 'kg', imperialUnit: 'lb', imperialDecimals: 1 },
};

/** Unit symbol for a quantity in the chosen system, e.g. ('temperature', 'imperial') → "°F". */
export function measureUnit(quantity: Quantity, system: UnitSystem): string {
  const def = QUANTITIES[quantity];
  return system === 'imperial' ? def.imperialUnit : def.metricUnit;
}

/** Numeric value in the chosen system; metric input passes through unchanged. */
export function convertMeasure(value: number, quantity: Quantity, system: UnitSystem): number {
  if (system !== 'imperial') return value;
  const def = QUANTITIES[quantity];
  const factor = 10 ** def.imperialDecimals;
  return Math.round(def.toImperial(value) * factor) / factor;
}

/** One-string form, e.g. "23 °C" / "73 °F"; the app-wide dash for missing values. */
export function formatMeasure(
  value: number | null | undefined,
  quantity: Quantity,
  system: UnitSystem,
): string {
  if (value == null) return '—';
  return `${convertMeasure(value, quantity, system)} ${measureUnit(quantity, system)}`;
}
